import React from 'react';
import { formatISO, isBefore, isWithinInterval, max, parseISO } from 'date-fns';
import { IconBoldCalendar } from 'design-system/icons';
import { formatStringList } from 'utils/formatting';
import { RentReportingFrequency, } from '../types';
export const PROCESSED = [
    'REPORTED',
    'PROCESSED',
];
export const INELIGIBLE = [
    'INELIGIBLE_PREDATES_REPORTING',
    'INELIGIBLE_INSUFFICIENT_DATA',
    'GHOST_INSUFFICIENT_DATA',
];
export const hasStatus = (data, status) => {
    if (Array.isArray(status)) {
        return (status.includes(data.experianReportingStatus) ||
            status.includes(data.equifaxReportingStatus) ||
            status.includes(data.transunionReportingStatus));
    }
    return (status === data.experianReportingStatus ||
        status === data.equifaxReportingStatus ||
        status === data.transunionReportingStatus);
};
export const RENTAL_FREQUENCIES = {
    [RentReportingFrequency.WEEKLY]: {
        label: 'Weekly',
        gradientKey: 'ultimate',
    },
    [RentReportingFrequency.FORTNIGHTLY]: {
        label: 'Fortnightly',
        gradientKey: 'darkPink',
    },
    [RentReportingFrequency.MONTHLY]: {
        label: 'Monthly',
        gradientKey: 'apricot',
    },
    [RentReportingFrequency.QUARTERLY]: {
        label: 'Quarterly',
        gradientKey: 'orange',
    },
    [RentReportingFrequency.BIANNUALLY]: {
        label: '6 months',
        gradientKey: 'darkPink',
    },
    [RentReportingFrequency.ANNUALLY]: {
        label: 'Yearly',
        gradientKey: 'lightPink',
    },
    // These types won’t be selectable in the frontend yet, adding for backwards compat in case we ever add
    [RentReportingFrequency.PERIODIC]: {
        label: 'Periodic',
        gradientKey: 'darkPurple',
    },
};
export const FREQUENCY_OPTIONS = [
    RentReportingFrequency.WEEKLY,
    RentReportingFrequency.FORTNIGHTLY,
    RentReportingFrequency.MONTHLY,
    RentReportingFrequency.QUARTERLY,
    RentReportingFrequency.BIANNUALLY,
    RentReportingFrequency.ANNUALLY,
].map((item) => ({
    label: RENTAL_FREQUENCIES[item].label,
    icon: <IconBoldCalendar gradientKey={RENTAL_FREQUENCIES[item].gradientKey}/>,
    value: item,
}));
export const RENT_REPORTING_REQUIRED_HISTORY = {
    [RentReportingFrequency.WEEKLY]: 'at least 8 payments in the last 6 months',
    [RentReportingFrequency.FORTNIGHTLY]: 'at least 8 payments in the last 6 months',
    [RentReportingFrequency.MONTHLY]: 'at least 6 payments in the last year',
    [RentReportingFrequency.QUARTERLY]: 'at least 3 payments in the last year',
    [RentReportingFrequency.BIANNUALLY]: 'at least 2 payments in the last year',
    [RentReportingFrequency.ANNUALLY]: 'at least 1 payment in the last year',
    [RentReportingFrequency.PERIODIC]: '',
};
export const RENT_REPORTING_AGENCIES = {
    EQUIFAX: {
        label: 'Equifax',
        id: 'EQUIFAX',
        image: require('../img/equifax.png'),
    },
    EXPERIAN: {
        label: 'Experian',
        id: 'EXPERIAN',
        image: require('../img/experian.png'),
    },
    TRANSUNION: {
        label: 'TransUnion',
        id: 'TRANSUNION',
        image: require('../img/transunion.png'),
    },
};
export var RentReportingNotTrackingAction;
(function (RentReportingNotTrackingAction) {
    RentReportingNotTrackingAction["RENT_REPORTING"] = "RentReporting";
    RentReportingNotTrackingAction["BROKEN_CONNECTIONS"] = "BrokenConnections";
    RentReportingNotTrackingAction["LOGINS_RECONSENT"] = "LoginsReconsent";
})(RentReportingNotTrackingAction || (RentReportingNotTrackingAction = {}));
const strings = {
    title: 'Rent reporting not tracking',
    actionRequired: 'Action required',
    manageReporting: 'Manage reporting',
    reconnectBank: 'Reconnect bank',
    checkBankConnection: 'Check the bank connection',
    tapToResolve: 'Tap to resolve',
};
const DEFAULT_NOT_TRACKING_INFO = {
    title: strings.title,
    description: 'Update details or start new tracking',
    cta: strings.actionRequired,
    action: RentReportingNotTrackingAction.RENT_REPORTING,
};
const UPGRADE_NOT_TRACKING_INFO = {
    ...DEFAULT_NOT_TRACKING_INFO,
    description: 'Upgrade to start tracking again',
};
const NOT_TRACKING_INFO = {
    ACCOUNT_NOT_SYNCED: {
        title: strings.title,
        description: 'The bank account you use to pay rent isn’t syncing. Refresh your connection to continue reporting',
        cta: strings.checkBankConnection,
        action: RentReportingNotTrackingAction.BROKEN_CONNECTIONS,
    },
    ACCOUNT_CLOSED_REMOVED: {
        title: strings.title,
        description: 'The bank account you use to pay rent has been closed or removed. Update your details so we can continue reporting your payments',
        cta: strings.manageReporting,
        action: RentReportingNotTrackingAction.RENT_REPORTING,
        mergeWithManageButton: true,
    },
    ACCOUNT_NOT_FOUND: {
        title: strings.title,
        description: 'Emma has stopped syncing with the bank account you use to pay rent. Reconnect your account to keep your rent reporting active',
        cta: strings.reconnectBank,
        action: RentReportingNotTrackingAction.RENT_REPORTING,
        mergeWithManageButton: true,
    },
    // The connection no longer exists in Emma, so BrokenConnections would be
    // empty — send the user to manage rent reporting instead.
    BANK_CONNECTION_NOT_FOUND: {
        title: strings.title,
        description: 'Emma has stopped syncing with the bank account you use to pay rent. Reconnect your account to keep your rent reporting active',
        cta: strings.reconnectBank,
        action: RentReportingNotTrackingAction.RENT_REPORTING,
        mergeWithManageButton: true,
    },
    BANK_CONNECTION_NEEDS_FIXED: {
        title: strings.title,
        description: 'You need to renew your bank account access, so that we can track and report your rent payments',
        cta: strings.checkBankConnection,
        action: RentReportingNotTrackingAction.BROKEN_CONNECTIONS,
    },
    BANK_CONNECTION_CONSENT_EXPIRED: {
        title: strings.title,
        description: 'Your bank consent has expired. Renew your access, so we can track and report your rent payments',
        cta: strings.tapToResolve,
        action: RentReportingNotTrackingAction.LOGINS_RECONSENT,
    },
    EXPECTED_PAYMENT_OVERDUE: {
        title: strings.title,
        description: 'Your payment is now overdue. Please make sure all the details are correct',
        cta: strings.manageReporting,
        action: RentReportingNotTrackingAction.RENT_REPORTING,
        showOnFeed: true,
        mergeWithManageButton: true,
    },
    MISSED_EXPECTED_PAYMENT_DATE: {
        title: strings.title,
        description: 'Your payment was not detected. Please make sure all the details are correct',
        cta: strings.manageReporting,
        action: RentReportingNotTrackingAction.RENT_REPORTING,
        showOnFeed: true,
        mergeWithManageButton: true,
    },
};
export const hasNotTrackingError = (data) => 
// only an explicit false is an error — a missing isTracking means the
// tracking status simply wasn't computed for this payload
!!data &&
    data.isTracking === false &&
    !!data.reportingAgencies?.length &&
    !!data.notTrackingReason;
export const shouldShowNotTrackingInfoOnFeed = (data) => hasNotTrackingError(data) &&
    !!data.notTrackingReason &&
    !!NOT_TRACKING_INFO[data.notTrackingReason]?.showOnFeed;
export const getNotTrackingInfo = (data) => {
    if (data.reportingAgencies?.length) {
        // fall back gracefully if the backend sends a reason this app version doesn't know
        if (data.notTrackingReason && data.notTrackingReason in NOT_TRACKING_INFO) {
            return NOT_TRACKING_INFO[data.notTrackingReason];
        }
        return DEFAULT_NOT_TRACKING_INFO;
    }
    return UPGRADE_NOT_TRACKING_INFO;
};
// Not-tracking errors render as either a standalone CTA button above the
// manage button, or as the manage button itself turning error-coloured
export const getNotTrackingButtonState = (data) => {
    const notTrackingInfo = hasNotTrackingError(data)
        ? getNotTrackingInfo(data)
        : undefined;
    return {
        ctaInfo: notTrackingInfo && !notTrackingInfo.mergeWithManageButton
            ? notTrackingInfo
            : undefined,
        manageButtonHasError: !!notTrackingInfo?.mergeWithManageButton,
    };
};
export const formatRentReportingAgencies = (array) => {
    if (!array)
        return '';
    const formattedAgencies = array.map((agency) => RENT_REPORTING_AGENCIES[agency]?.label ||
        agency.charAt(0).toUpperCase() + agency.slice(1).toLowerCase());
    return formatStringList(formattedAgencies);
};
export const getSections = (rentReportingData) => {
    const sections = [];
    if (!rentReportingData)
        return sections;
    const { createdAt, startDate, endDate, transunionLastReportDate, equifaxLastReportDate, experianLastReportDate, } = rentReportingData;
    let firstReportedDate = startDate;
    let setupDate = createdAt;
    const oldestReportedTransaction = rentReportingData.rentTransactions?.findLast((tr) => hasStatus(tr, PROCESSED));
    const oldestPendingTransaction = rentReportingData.rentTransactions?.findLast((tr) => hasStatus(tr, 'PENDING'));
    if (oldestReportedTransaction) {
        if (!firstReportedDate ||
            isBefore(oldestReportedTransaction.bookingDate, firstReportedDate)) {
            firstReportedDate = oldestReportedTransaction.bookingDate;
        }
    }
    if (oldestPendingTransaction) {
        if (!firstReportedDate ||
            isBefore(oldestPendingTransaction.bookingDate, firstReportedDate)) {
            firstReportedDate = oldestPendingTransaction.bookingDate;
        }
    }
    if (firstReportedDate && isBefore(firstReportedDate, setupDate)) {
        setupDate = firstReportedDate;
    }
    const groupedTransactions = (rentReportingData.rentTransactions ?? []).reduce((acc, curr) => {
        if (isBefore(curr.bookingDate, setupDate)) {
            acc.beforeSetup.push(curr);
            return acc;
        }
        if (!firstReportedDate) {
            acc.afterSetup.push(curr);
            return acc;
        }
        if (firstReportedDate &&
            (hasStatus(curr, PROCESSED) || hasStatus(curr, 'PENDING'))) {
            acc.reporting.push(curr);
            return acc;
        }
        if (isWithinInterval(curr.bookingDate, {
            start: setupDate,
            end: firstReportedDate,
        })) {
            acc.afterSetup.push(curr);
            return acc;
        }
        acc.reporting.push(curr);
        return acc;
    }, {
        beforeSetup: [],
        afterSetup: [],
        reporting: [],
    });
    if (endDate) {
        sections.push({
            status: 'CANCELLED',
            date: endDate,
        });
    }
    else if (!rentReportingData.isTracking) {
        const lastReportDateMap = {
            EQUIFAX: equifaxLastReportDate,
            EXPERIAN: experianLastReportDate,
            TRANSUNION: transunionLastReportDate,
        };
        const datesForTier = rentReportingData.reportingAgencies?.map((agency) => parseISO(lastReportDateMap[agency] ?? new Date().toISOString()));
        sections.push({
            status: 'STOPPED',
            date: datesForTier && datesForTier.length
                ? formatISO(max(datesForTier))
                : formatISO(new Date()),
        });
    }
    if (firstReportedDate) {
        if (groupedTransactions.reporting.length) {
            sections.push(...groupedTransactions.reporting.map((transaction, index) => ({
                ...transaction,
                itemIndex: index,
                itemsLength: groupedTransactions.reporting.length,
            })));
        }
        sections.push({
            status: 'REPORTING',
            date: firstReportedDate,
        });
    }
    else {
        sections.push({
            status: 'PREPARING',
            date: setupDate,
        });
    }
    if (groupedTransactions.afterSetup.length) {
        sections.push(...groupedTransactions.afterSetup.map((transaction, index) => ({
            ...transaction,
            itemIndex: index,
            itemsLength: groupedTransactions.afterSetup.length,
        })));
    }
    sections.push({
        status: 'SETUP',
        date: setupDate,
    }, ...groupedTransactions.beforeSetup.map((transaction, index) => ({
        ...transaction,
        itemIndex: index,
        itemsLength: groupedTransactions.beforeSetup.length,
    })));
    return sections;
};
